import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  type SimulationDetail,
  type StageRow,
  useSimulationStore,
} from '../store/simulationStore.js';
import { getApiOrigin, getFetchAuthHeaders } from '../lib/api.js';

export type SessionPhasePill = {
  id: string;
  label: string;
  state: 'completed' | 'active' | 'upcoming';
};

function storageKey(simulationId: string, stageId: string) {
  return `ig-elapsed-${simulationId}-${stageId}`;
}

function languageCheckStorageKey(simulationId: string) {
  return `ig-elapsed-${simulationId}-language-check`;
}

function languageCheckStartedStorageKey(simulationId: string) {
  return `ig-language-check-started-${simulationId}`;
}

const INTERVIEW_STAGE_ROUTE_RE = /\/interview\/[^/]+\/(?:stage|call)\/\d+$/;
const INTERVIEW_FEEDBACK_ROUTE_RE = /\/interview\/[^/]+\/stage\/\d+\/feedback$/;
const INTERVIEW_LANGUAGE_CHECK_ROUTE_RE = /\/interview\/[^/]+\/(?:language-check|call\/language)$/;

/**
 * Module-level callback that ActiveInterviewPage registers so that
 * endStageWithoutConfirm can send any typed-but-not-yet-submitted answer
 * before marking the stage as complete. Returns false when a pending answer
 * could not be persisted.
 */
let _sendPendingAnswerBeforeComplete: (() => Promise<boolean>) | null = null;

export function registerPendingAnswerSender(fn: (() => Promise<boolean>) | null) {
  _sendPendingAnswerBeforeComplete = fn;
}

function interviewTypeLabel(code: string) {
  const labels: Record<string, string> = {
    HR_SCREENING: 'HR Screening',
    TECHNICAL: 'Technical',
    MANAGERIAL: 'Managerial',
    FINAL_ROUND: 'Final Round',
    CULTURE_FIT: 'Culture Fit',
  };
  return labels[code] ?? code;
}

function formatDurationShort(sec: number | null | undefined) {
  if (sec == null) return null;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function useSimulation() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { id: simulationId, stageNumber: stageNumberParam } = useParams<{
    id: string;
    stageNumber?: string;
  }>();
  const isFeedbackRoute = useMemo(
    () => INTERVIEW_FEEDBACK_ROUTE_RE.test(location.pathname),
    [location.pathname],
  );

  const trackElapsed = useMemo(
    () => INTERVIEW_STAGE_ROUTE_RE.test(location.pathname) && !isFeedbackRoute,
    [location.pathname, isFeedbackRoute],
  );

  const isLanguageCheckRoute = useMemo(
    () => INTERVIEW_LANGUAGE_CHECK_ROUTE_RE.test(location.pathname),
    [location.pathname],
  );

  const sessionControlsEnabled = trackElapsed || isLanguageCheckRoute;

  const loadSimulation = useSimulationStore((s) => s.loadSimulation);
  const completeStage = useSimulationStore((s) => s.completeStage);
  const simulation = useSimulationStore((s) => s.currentSimulation);
  const currentStage = useSimulationStore((s) => s.currentStage);
  const messages = useSimulationStore((s) => s.messages);
  const languageCheckTotal = useSimulationStore((s) => s.languageCheckTotal);
  const languageCheckCurrent = useSimulationStore((s) => s.languageCheckCurrent);

  const stageNumber = stageNumberParam ? Number.parseInt(stageNumberParam, 10) : NaN;

  // Prevent React StrictMode's double-invocation from firing duplicate loadSimulation calls.
  // Key includes stageNumber so navigating to a different stage still re-fetches.
  const loadedSimKey = useRef<string | null>(null);

  useEffect(() => {
    if (!simulationId) return;
    const key = `${simulationId}:${Number.isFinite(stageNumber) ? stageNumber : ''}`;
    if (loadedSimKey.current === key) return;
    loadedSimKey.current = key;
    void loadSimulation(simulationId, Number.isFinite(stageNumber) ? stageNumber : undefined);
  }, [loadSimulation, simulationId, stageNumber]);

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const shouldTrack = trackElapsed || isLanguageCheckRoute;
    if (!shouldTrack || !simulationId) {
      setElapsedSeconds(0);
      return;
    }

    const key = isLanguageCheckRoute
      ? languageCheckStorageKey(simulationId)
      : currentStage?.id
        ? storageKey(simulationId, currentStage.id)
        : null;
    if (!key) {
      setElapsedSeconds(0);
      return;
    }

    if (isLanguageCheckRoute) {
      const started = sessionStorage.getItem(languageCheckStartedStorageKey(simulationId)) === '1';
      if (!started) {
        setElapsedSeconds(0);
        return;
      }
    }

    const existing = Number.parseInt(sessionStorage.getItem(key) ?? '0', 10) || 0;
    setElapsedSeconds(existing);

    intervalRef.current = setInterval(() => {
      setElapsedSeconds((prev) => {
        const next = prev + 1;
        sessionStorage.setItem(key, String(next));
        return next;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [trackElapsed, isLanguageCheckRoute, simulationId, currentStage?.id]);

  const stageName = useCallback(
    (n: number) =>
      t(`interview.feedback.stageLabels.${n}`, {
        defaultValue: t('interview.feedback.stageLabelFallback', { n }),
      }),
    [t],
  );

  const phases: SessionPhasePill[] = useMemo(() => {
    if (!simulation?.stages?.length) return [];
    const sorted = [...simulation.stages].sort((a, b) => a.stageNumber - b.stageNumber);
    const activeNum = Number.isFinite(stageNumber)
      ? stageNumber
      : sorted.find((s) => s.status === 'UNLOCKED')?.stageNumber ??
        sorted.find((s) => s.status !== 'COMPLETED')?.stageNumber ??
        sorted[sorted.length - 1]?.stageNumber ??
        1;

    const stagePills = sorted.map((s): SessionPhasePill => {
      let state: SessionPhasePill['state'] = 'upcoming';
      if (s.status === 'COMPLETED' || s.stageNumber < activeNum) state = 'completed';
      else if (s.stageNumber === activeNum && !isLanguageCheckRoute && !isFeedbackRoute)
        state = 'active';
      return { id: s.id, label: stageName(s.stageNumber), state };
    });

    if (simulation.assessmentLanguage) {
      const checkLabel = t('interview.header.languageCheckPill', {
        lang: simulation.assessmentLanguage,
        defaultValue: `${simulation.assessmentLanguage} Check`,
      });
      const languageCheckDone = Boolean(
        simulation.languageAssessment ||
        simulation.languageCheckSkippedAt ||
        (simulation.languageAssessmentStatus && simulation.languageAssessmentStatus !== 'none'),
      );
      stagePills.unshift({
        id: 'language-check',
        label: checkLabel,
        state: isLanguageCheckRoute
          ? 'active'
          : languageCheckDone
            ? 'completed'
            : 'upcoming',
      });
    }

    return stagePills;
  }, [simulation, stageNumber, isLanguageCheckRoute, isFeedbackRoute, stageName, t]);

  const currentPhase = useMemo(() => {
    if (isLanguageCheckRoute) {
      const langCode = simulation?.assessmentLanguage ?? 'EN';
      const langLabel = t(`interview.languageCheck.languages.${langCode}`, {
        defaultValue:
          langCode === 'PL' ? 'Polish' : langCode === 'DE' ? 'German' : 'English',
      });
      return t('interview.header.languageCheckPhase', {
        language: langLabel,
        defaultValue: `Language Check — ${langLabel}`,
      });
    }
    if (!simulation?.stages?.length) return t('interview.header.defaultPhase', { defaultValue: 'Interview' });
    const sorted = [...simulation.stages].sort((a, b) => a.stageNumber - b.stageNumber);
    const n = Number.isFinite(stageNumber)
      ? stageNumber
      : sorted.find((s) => s.status === 'UNLOCKED')?.stageNumber ??
        sorted.find((s) => s.status !== 'COMPLETED')?.stageNumber ??
        sorted[sorted.length - 1]?.stageNumber ??
        1;
    return stageName(n);
  }, [simulation, stageNumber, isLanguageCheckRoute, stageName, t]);

  const progress = useMemo(() => {
    if (isLanguageCheckRoute) return null;
    const total = simulation?.stages?.length ?? 3;
    const sorted = simulation?.stages
      ? [...simulation.stages].sort((a, b) => a.stageNumber - b.stageNumber)
      : [];
    const current = Number.isFinite(stageNumber)
      ? stageNumber
      : sorted.find((s) => s.status === 'UNLOCKED')?.stageNumber ??
        sorted.find((s) => s.status !== 'COMPLETED')?.stageNumber ??
        sorted[sorted.length - 1]?.stageNumber ??
        1;
    return { current, total };
  }, [simulation, stageNumber, isLanguageCheckRoute]);

  const staticDurationLabel = useMemo(
    () => (isFeedbackRoute ? formatDurationShort(currentStage?.durationSeconds) : null),
    [isFeedbackRoute, currentStage?.durationSeconds],
  );

  const completedPhases = useMemo(
    () => simulation?.stages.filter((s) => s.status === 'COMPLETED').map((s) => s.stageNumber) ?? [],
    [simulation],
  );

  const isLanguageCheckRequired = useMemo(() => {
    if (!simulation?.assessmentLanguage) return false;
    return true;
  }, [simulation]);

  const nextStageNumber = useMemo(() => {
    if (!simulation?.stages?.length) return null as number | null;
    const pending = simulation.stages.find((s) => s.status !== 'COMPLETED');
    return pending?.stageNumber ?? null;
  }, [simulation]);

  const simulationConfig = useMemo(
    () =>
      simulation
        ? {
            role: simulation.role,
            interviewType: interviewTypeLabel(simulation.interviewType),
            primaryLanguage: simulation.primaryLanguage,
            assessmentLanguage: simulation.assessmentLanguage,
          }
        : null,
    [simulation],
  );

  const isStageComplete = useMemo(() => {
    if (!currentStage?.questionsCount) return false;
    const answered = messages.filter((m) => m.role === 'USER').length;
    return answered >= currentStage.questionsCount;
  }, [currentStage?.questionsCount, messages]);

  const questionCounterLabel = useMemo(() => {
    if (isFeedbackRoute) return null;
    if (isLanguageCheckRoute) {
      const total = languageCheckTotal;
      const current = languageCheckCurrent;
      return t('interview.header.questionCounter', {
        current,
        total,
        defaultValue: `Question ${current} of ${total}`,
      });
    }
    if (!trackElapsed || !currentStage?.questionsCount) return null;
    const answered = messages.filter((message) => message.role === 'USER').length;
    const current = Math.max(1, Math.min(currentStage.questionsCount, answered + 1));
    return t('interview.header.questionCounter', {
      current,
      total: currentStage.questionsCount,
      defaultValue: `Question ${current} of ${currentStage.questionsCount}`,
    });
  }, [
    currentStage?.questionsCount,
    elapsedSeconds,
    isFeedbackRoute,
    isLanguageCheckRoute,
    languageCheckCurrent,
    languageCheckTotal,
    messages,
    simulationId,
    t,
    trackElapsed,
  ]);

  const [stageTransition, setStageTransition] = useState<{ stageNumber: number; totalStages: number; skipped?: boolean } | null>(null);

  /**
   * True only when the user answered every question in the current stage.
   * Anything less (including zero) means the stage was ended early and counts
   * as skipped — no feedback is generated for it.
   */
  const isStageFullyAnswered = useCallback(() => {
    const state = useSimulationStore.getState();
    const answers = state.messages.filter((m) => m.role === 'USER').length;
    const total = state.currentStage?.questionsCount ?? 0;
    return total > 0 && answers >= total;
  }, []);

  const endStageWithoutConfirm = useCallback(async () => {
    if (isLanguageCheckRoute) {
      if (!simulationId) return;
      navigate(`/interview/${simulationId}/stage/1`);
      return;
    }
    if (!currentStage?.id || !simulationId) return;
    // Ending a stage before every question is answered = skipped, not
    // completed: a partial transcript can't be fairly evaluated, so the
    // overlay says "skipped" and the backend generates no feedback. The
    // server re-checks this itself; this only drives the overlay copy.
    const skipped = !isStageFullyAnswered();
    // Celebrate the finished stage with the full transition overlay —
    // same duration as the between-stage and post-language-check experience.
    const totalStages = Math.max(
      useSimulationStore.getState().currentSimulation?.stages?.length ?? 3,
      3,
    );
    if (Number.isFinite(stageNumber)) {
      setStageTransition({ stageNumber, totalStages, skipped });
    }
    try {
      // Send any typed-but-not-yet-submitted answer before completing
      if (_sendPendingAnswerBeforeComplete) {
        const pendingSaved = await _sendPendingAnswerBeforeComplete();
        if (!pendingSaved) {
          toast.error(
            t('interview.active.answerSaveFailed', {
              defaultValue: 'Could not save your answer. Please try again.',
            }),
          );
          return;
        }
      }
      // A pending answer may have just been saved above — recompute so a
      // last-second typed answer flips the stage back to a real completion.
      const finalSkipped = skipped && !isStageFullyAnswered();
      if (finalSkipped !== skipped && Number.isFinite(stageNumber)) {
        setStageTransition({ stageNumber, totalStages, skipped: finalSkipped });
      }
      await Promise.all([
        completeStage(currentStage.id, elapsedSeconds, { skipped: finalSkipped }),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]);
      // completeStage calls loadSimulation, so currentSimulation now reflects
      // the newly unlocked next stage — route there instead of to the report,
      // which is still generating in the background.
      if (Number.isFinite(stageNumber)) {
        const updatedSim = useSimulationStore.getState().currentSimulation;
        const sortedStages = [...(updatedSim?.stages ?? [])].sort(
          (a, b) => a.stageNumber - b.stageNumber,
        );
        const nextStage = sortedStages.find((s) => s.stageNumber === stageNumber + 1);
        navigate(
          nextStage
            ? `/interview/${simulationId}/stage/${nextStage.stageNumber}`
            : '/dashboard',
        );
      }
    } finally {
      setStageTransition(null);
    }
  }, [
    completeStage,
    currentStage?.id,
    elapsedSeconds,
    isLanguageCheckRoute,
    isStageFullyAnswered,
    navigate,
    simulationId,
    stageNumber,
    t,
  ]);

  const completeStageBestEffort = useCallback(async () => {
    if (isLanguageCheckRoute || !currentStage?.id || !simulationId) return;
    try {
      const apiOrigin = getApiOrigin();
      const url = apiOrigin
        ? `${apiOrigin}/api/v1/stages/${currentStage.id}/complete`
        : `/api/v1/stages/${currentStage.id}/complete`;
      await fetch(url, {
        method: 'PATCH',
        keepalive: true,
        headers: {
          ...(await getFetchAuthHeaders({ contentType: 'application/json' })),
        },
        body: JSON.stringify({
          durationSeconds: elapsedSeconds,
          skipped: !isStageFullyAnswered(),
        }),
      });
    } catch {
      // Best-effort only — if this fails during page teardown there's nothing more we can do.
    }
  }, [currentStage?.id, elapsedSeconds, isLanguageCheckRoute, isStageFullyAnswered, simulationId]);

  const onEndStage = useCallback(async () => {
    if (isLanguageCheckRoute) {
      await endStageWithoutConfirm();
      return;
    }
    const ok = window.confirm(
      isStageFullyAnswered()
        ? t('interview.header.endStageConfirmText', {
            defaultValue: 'Are you sure? This stage will be marked as complete.',
          })
        : t('interview.header.skipStageConfirmText', {
            defaultValue:
              'Are you sure? You have not answered every question — this stage will be marked as skipped and no feedback will be generated.',
          }),
    );
    if (!ok) return;
    await endStageWithoutConfirm();
  }, [endStageWithoutConfirm, isLanguageCheckRoute, isStageFullyAnswered, t]);

  return {
    simulationId,
    stageNumber: Number.isFinite(stageNumber) ? stageNumber : null,
    simulation: simulation as SimulationDetail | null,
    currentStage: currentStage as StageRow | null,
    currentPhase,
    phases,
    elapsedSeconds,
    completedPhases,
    isLanguageCheckRequired,
    isLanguageCheckRoute,
    nextStageNumber,
    simulationConfig,
    onEndStage,
    endStageWithoutConfirm,
    stageTransition,
    completeStageBestEffort,
    endActionLabel: isLanguageCheckRoute
      ? t('common.cancel', { defaultValue: 'Cancel' })
      : t('interview.header.endStage', { defaultValue: 'End Stage' }),
    sessionControlsEnabled,
    isStageComplete,
    progress,
    staticDurationLabel,
    questionCounterLabel,
  };
}
